import { Component } from '@theme/component';

/**
 * <frame-component> — the "Load more" half of the Frame widget
 * (sections/frame.liquid).
 *
 * Frames past the section's `visible_frames` count are hidden by CSS while the
 * component carries `frame--collapsed` (the cutoff rule is inlined by the
 * section, which is the only place that knows the number). Clicking the button
 * lifts the collapse and hands the newly shown frames to the theme's
 * scroll-reveal system, so they fade in the way every other widget does
 * instead of appearing all at once.
 *
 * The section only loads this module when it has something to collapse, but a
 * page can hold several Frame sections and the module upgrades all of them —
 * so every hook here is optional and `expand` is inert on a section that has
 * no collapsed frames.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [grid] - The frame grid.
 * @property {HTMLElement} [loadMore] - The button's wrapper, retired on expand.
 * @property {HTMLButtonElement} [loadMoreButton] - The "Load more" button.
 *
 * @extends {Component<Refs>}
 */
class FrameComponent extends Component {
  /* Mirrors MAX_STAGGER_ORDER in motion.js: caps the cascade so a long batch
     tops out around a second of delay instead of queueing seconds of
     invisible content. */
  #maxStaggerOrder = 11;

  /**
   * Reveals the collapsed frames and retires the button.
   *
   * @param {Event} [event]
   */
  expand = (event) => {
    event?.preventDefault();

    if (!this.classList.contains('frame--collapsed')) return;

    const collapsed = this.#collapsedPanels();

    /* Dropping motion-revealed is what re-arms the fade: motion.js watches the
       class attribute of the items it has registered and re-observes any that
       lose it. These panels were revealed along with the rest of the grid's
       group, but a display:none element has no box and never intersects, so
       they have been sitting in their hidden state ever since.

       Their --motion-order comes from their place in the grid, which would
       leave the seventh frame waiting out 600ms of a stagger it no longer
       shares; the batch restarts the cascade at zero. */
    collapsed.forEach((panel, index) => {
      panel.style.setProperty('--motion-order', String(Math.min(index, this.#maxStaggerOrder)));
      panel.classList.remove('motion-revealed');
    });

    /* Only now do they get a box. motion.js re-observes them on the microtask
       that follows this handler, by which point they are laid out and can
       intersect — the ones on screen fade in, the rest as they are scrolled to. */
    this.classList.remove('frame--collapsed');

    const { loadMore, loadMoreButton } = this.refs;

    loadMoreButton?.setAttribute('aria-expanded', 'true');
    if (loadMore) loadMore.hidden = true;

    /* The button that held focus is gone; hand focus to the first new frame
       rather than let it fall back to the top of the document. preventScroll
       so the page does not jump over the fade-in. */
    const [first] = collapsed;

    if (first) {
      first.setAttribute('tabindex', '-1');
      first.focus({ preventScroll: true });
    }
  };

  /**
   * The panels the collapse rule is hiding, in DOM order.
   *
   * @returns {HTMLElement[]}
   */
  #collapsedPanels() {
    const { grid } = this.refs;
    const visible = Number(this.dataset.visibleFrames);

    if (!grid || !Number.isFinite(visible)) return [];

    return /** @type {HTMLElement[]} */ ([...grid.querySelectorAll(':scope > .frame__panel')]).slice(visible);
  }
}

if (!customElements.get('frame-component')) {
  customElements.define('frame-component', FrameComponent);
}
